import { createTheme } from '@material-ui/core/styles';

export enum ColorTypeEnum {
  PRIMARY = 'primary',
  WARNING = 'warning',
  ERROR = 'error',
}

const gillsans = {
  fontFamily: 'GillSans',
  fontStyle: 'normal',
  fontWeight: 400,
  src: `url("/fonts/GillSans.woff2") format('woff2')`,
};

const gillsansbold = {
  fontFamily: 'GillSans',
  fontStyle: 'normal',
  fontWeight: 700,
  src: `url("/fonts/GillSansBold.woff2") format('woff2')`,
};

const gillsanssemibold = {
  fontFamily: 'GillSans',
  fontStyle: 'normal',
  fontWeight: 600,
  src: `url("/fonts/GillSansSemiBold.woff2") format('woff2'), url("/fonts/GillSansSemiBold.woff") format('woff')`,
};

const gillsansnovainline = {
  fontFamily: 'GillSansNovaInline',
  fontStyle: 'normal',
  fontWeight: 600,
  src: `url("/fonts/GillSansNovaInlineRegular.woff2") format('woff2'), url("/fonts/GillSansNovaInlineRegular.woff") format('woff')`,
};

export const fonts = {
  primary: ['"GillSans"', '"Helvetica"', '"Arial"', 'sans-serif'].join(','),
  secondary: ['"Open Sans"', '"Helvetica"', '"Arial"', 'sans-serif'].join(','),
  titles: ['"GillSansNovaInline"', '"GillSans"', '"Helvetica"', '"Arial"', 'sans-serif'].join(','),
};

export const colors = {
  primary: '#137181',
  primaryBlueBase: '#1AA1E1',
  white: '#FFFFFF',
  black: '#000000',
  inputGrey: '#757575',
  grey3: '#828282',
  greyLightest: '#F8F9FA',
  greyLighten10: '#CFD4DA',
  greyLighten20: '#EDEDED',
  gray700: '#737F8F',
  neutralGray: '#BFBFBF',
  grey050: '#F0F2F5',
  grey100: '#E9EBEE',
  grey500: '#A3AAB5',
  grey700: '#737F8F',
  grey900: '#2B3648',
  greyBlue: '#868E96',
  hoveredBlue: '#1580B2',
  lightBlue: '#A0D6DE',
  neutral800: '#566271',
  lightRed: '#FF3B30',
  red: '#FF453A',
  redSoft: '#EB7585',
  green: '#3DC55E',
  greenSoft: '#8FC69A',
  orange: '#FABF40',
  darkOrange: '#FF9F0A',
  greenPastel: '#D6E9D8',
  redPastel: '#F6C7CD',
};

const theme = createTheme({
  unstable_strictMode: true,
  props: {
    MuiOutlinedInput: {
      notched: false,
    },
  },
  palette: {
    primary: {
      main: colors.primary,
      contrastText: '#fff',
    },
    secondary: {
      main: '#E3147E',
      contrastText: '#000',
    },
  },
  overrides: {
    MuiBackdrop: {
      root: {
        backgroundColor: '#2b3648cc',
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 0,
      },
    },
    MuiDivider: {
      root: {
        marginLeft: 'auto',
        marginRight: 'auto',
        backgroundColor: '#EDEDED',
        height: 2,
      },
      middle: {
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'calc(100% - 120px)',
      },
    },
    MuiButton: {
      outlined: {
        borderRadius: 0,
        border: '1px solid black',
        fontWeight: 600,
      },
      outlinedPrimary: {
        border: `1px solid ${colors.primary}`,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
        '&:active': {
          boxShadow: 'none',
        },
      },
      containedPrimary: {
        color: 'white',
        backgroundColor: colors.grey900,

        '&:hover': {
          backgroundColor: colors.primary,
        },
      },
      sizeLarge: {
        padding: '16px 32px',
      },
    },
    MuiCssBaseline: {
      '@global': {
        '@font-face': [gillsans, gillsansbold, gillsanssemibold, gillsansnovainline],
      },
    },
    MuiMenuItem: {
      root: {
        height: '60px',

        '&:hover': {
          boxShadow: `inset 6px 0px 0px 0px ${colors.primary}`,
        },
      },
    },
    MuiLink: {
      root: {
        color: colors.grey900,
        textTransform: 'uppercase',
        textDecoration: 'none',
        fontSize: '0.8em',

        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    MuiListItem: {
      button: {
        height: '60px',

        '&:hover': {
          backgroundColor: colors.grey050,
        },
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: '42px',
      },
    },
    MuiTableCell: {
      head: {
        fontWeight: 'bold',
      },
      footer: {
        borderBottom: 'none',
      },
    },
    MuiTablePagination: {
      selectIcon: {
        fontSize: 'inherit',
        right: 7,
      },
    },
    MuiInputLabel: {
      root: {
        color: 'inherit',
      },
      outlined: {
        fontSize: 18,
        marginBottom: 5,
        position: 'initial',
        transform: 'none',

        '&$shrink': {
          transform: 'none',
        },
      },
    },
    MuiChip: {
      root: {
        backgroundColor: colors.lightBlue,
        borderRadius: '5px',
        margin: '0 5px',
        color: colors.white,
      },
    },
    MuiInput: {
      underline: {
        '&.Mui-disabled::before': {
          borderBottomStyle: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: 'rgba(0, 0, 0, 0.87)', // Same as default hover
      },
    },
    MuiSelect: {
      icon: {
        fontSize: '2em',
        top: 'inherit',
        right: '8px',
      },
      iconOutlined: {
        right: '8px',
      },
    },
    MuiStepIcon: {
      root: {
        width: '2em',
        height: '2em',
        fill: 'transparent',
        overflow: 'visible',

        '& > circle': {
          strokeWidth: '2px',
          stroke: 'currentColor',
        },
        '&$completed > path': {
          strokeWidth: '2px',
          stroke: 'currentColor',
        },
      },
      text: {
        fill: 'currentColor',
      },
    },
    MuiStepLabel: {
      root: {
        textTransform: 'uppercase',
      },
      label: {
        '&$alternativeLabel': {
          marginTop: 8,
          fontWeight: 600,
        },
        '&$active': {
          color: colors.primary,
        },
        '&$completed': {
          color: colors.primary,
        },
      },
      labelContainer: {
        width: '80%',
      },
    },
    MuiStepper: {
      root: {
        minWidth: '70%',
      },
    },
    MuiStepConnector: {
      alternativeLabel: {
        top: '22px',
        left: 'calc(-50% + 26px)',
        right: 'calc(50% + 26px)',
      },
      lineHorizontal: {
        borderTopWidth: '4px',
      },
      active: {
        '& $line': {
          borderColor: colors.primary,
        },
      },
      completed: {
        '& $line': {
          borderColor: colors.primary,
        },
      },
    },
    RaFilterButton: {
      root: {
        borderRadius: 0,
        border: '1px solid black',
        height: '30px',
        margin: '0 3px',
        '& .add-filter': {
          color: 'black',

          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: fonts.primary,
    h1: {
      fontSize: '2rem',
      fontWeight: 400,
      fontFamily: fonts.titles,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
  },
});

export default theme;
